import React from 'react'

import { type BrokersEnrollmentWithPaymentsOrCoverages } from 'utils/web_api.schemas'

import { formatMoney } from '../../utils'
import { AccountAmountVisual } from '../AccountAmountVisual'
import { MathVisual } from '../MathVisual'
import { ReimbursementPurpose } from './types'

interface ReimbursementAmountLimitProps {
  enrollments: BrokersEnrollmentWithPaymentsOrCoverages[]
  getMaximumAmount: () => number
  reimbursementType: string
  selectedEnrollmentId?: string
  selectedPurpose: string
  reimbursementOnlyApplies: (enrollment: BrokersEnrollmentWithPaymentsOrCoverages) => boolean
}

export const ReimbursementAmountLimit: React.FC<
  ReimbursementAmountLimitProps
> = ({
  enrollments,
  getMaximumAmount,
  reimbursementType,
  selectedEnrollmentId,
  selectedPurpose,
  reimbursementOnlyApplies,
}) => {
  const maximumAmount = formatMoney(getMaximumAmount())

  const getLimitDisplay = () => {
    if (reimbursementType === 'hsa') {
      return (
        <AccountAmountVisual
          amount={maximumAmount}
          label='HSA Account Total'
          tooltipDescription='This was marked as an HSA Reimbursement by this member.'
        />
      )
    }

    if (selectedPurpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) {
      return (
        <AccountAmountVisual
          amount={maximumAmount}
          label='ICHRA Balance Total'
          tooltipDescription='For QME, we can only reimburse up to the current ICHRA Balance Total (minus any holds or pending reimbursements).'
        />
      )
    } else if (
      selectedPurpose === ReimbursementPurpose.PREMIUM &&
      selectedEnrollmentId
    ) {
      const selectedEnrollment = enrollments.filter(
        (e) => e.id === selectedEnrollmentId
      )[0]
      const enrollmentStartDate = new Date(
        selectedEnrollment.coverage_start_date || ''
      ).toISOString()
      const enrollmentYear = new Date(enrollmentStartDate).getUTCFullYear()
      const currentYear = new Date().getFullYear()

      const prevYearEnrollment = enrollmentYear === currentYear - 1

      return reimbursementOnlyApplies(selectedEnrollment)
        ? (
          <AccountAmountVisual
            amount={maximumAmount}
            label='ICHRA Balance Total'
            tooltipDescription='This member is reimbursements-only, meaning we can only reimburse up to their account balance.'
          />
          )
        : (
          <MathVisual
            operationSymbol='-'
            valueOne={selectedEnrollment.cost_of_coverage?.value || '0'}
            valueOneLabel={
            prevYearEnrollment
              ? `${enrollmentYear} Cost of Coverage`
              : 'YTD Cost of Coverage'
          }
            valueTwo={
            selectedEnrollment.enrollment_payments_total?.value || '0'
          }
            valueTwoLabel={
            prevYearEnrollment
              ? `${enrollmentYear} Enrollment Payments`
              : 'YTD Enrollment Payments'
          }
            result={maximumAmount}
            resultLabel='Reimbursement Amount'
          />
          )
    }

    return undefined
  }

  return (
    <div className='mt-6'>
      <label className='label-default font-medium text-ds-text-secondary'>
        Reimbursement Amount Limit
      </label>
      <div className='flex gap-x-4 mt-2'>{getLimitDisplay()}</div>
    </div>
  )
}
